// Contacts with marketing consent (README scope 5): search, add / edit, consent with its opt-in source (audited),
// STOP shown, open the chat, delete (admins, soft). Agents and admins edit; viewers read.
import { rpc, sb } from "../sb.ts";
import { waPhone } from "../../supabase/functions/_shared/phone.ts";
import { t } from "../i18n.ts";
import { fmtTime, h, mount, toast } from "../ui.ts";

interface C { id: string; name: string; wa_id: string | null; email: string | null; lang: string | null; tags: string[]; notes: string | null;
  source: string | null; marketing_ok: boolean; marketing_at: string | null; marketing_by: string | null; marketing_how: string | null;
  wa_stop_at: string | null; created_at: string }

export async function contacts(view: HTMLElement, role: string) {
  const canWrite = role === "admin" || role === "agent";
  const { data: st } = await sb.from("settings").select("default_cc").maybeSingle();
  const cc = st?.default_cc ?? "";
  const box = h("section", { class: "page" });
  const q = h("input", { type: "search", placeholder: t("Search name, number, email or tag") });
  const filter = h("select", {}, [["", t("Everyone")], ["ok", t("Marketing: yes")], ["no", t("Marketing: no")], ["stop", t("Replied STOP")]]
    .map(([v, l]) => h("option", { value: v }, l)));
  const listEl = h("div", {});
  const edit = h("div", {});
  mount(view, box);
  mount(box, h("h2", {}, t("Contacts")),
    h("div", { class: "row" }, q, filter, canWrite && h("button", { class: "primary", onclick: () => form(null) }, "+ " + t("New contact"))),
    edit, listEl);
  let timer: number | undefined;
  q.addEventListener("input", () => { clearTimeout(timer); timer = window.setTimeout(load, 250); });
  filter.addEventListener("change", load);

  async function load() {
    let req = sb.from("contacts").select("*").order("updated_at", { ascending: false }).limit(200);
    const s = q.value.trim().replace(/[%,()]/g, " ");
    if (s) {
      const d = s.replace(/\D/g, "");
      req = req.or([`name.ilike.%${s}%`, `email.ilike.%${s}%`, d.length >= 3 ? `wa_id.like.%${d}%` : "", `tags.cs.{${s.replace(/[{}"]/g, "")}}`].filter(Boolean).join(","));
    }
    if (filter.value === "ok") req = req.eq("marketing_ok", true);
    if (filter.value === "no") req = req.eq("marketing_ok", false);
    if (filter.value === "stop") req = req.not("wa_stop_at", "is", null);
    const { data, error } = await req;
    if (error) return mount(listEl, h("p", { class: "err" }, error.message));
    const rows = data as C[];
    mount(listEl, rows.length ? h("table", { class: "contacts" }, h("tbody", {}, rows.map((c) => h("tr", {},
      h("td", {}, h("b", { dir: "auto" }, c.name || "—"), c.tags?.length ? h("div", {}, c.tags.map((x) => h("span", { class: "tag" }, x))) : null),
      h("td", { dir: "ltr" }, c.wa_id ? "+" + c.wa_id : "", c.email && h("div", { class: "muted" }, c.email)),
      h("td", {}, c.wa_stop_at ? h("span", { class: "pill bad", title: fmtTime(c.wa_stop_at) }, "STOP")
        : c.marketing_ok ? h("span", { class: "pill ok", title: `${c.marketing_how ?? ""} · ${c.marketing_by ?? ""}` }, t("Marketing: yes"))
        : h("span", { class: "pill" }, t("Marketing: no"))),
      h("td", {}, c.wa_id && h("a", { href: `?wa=${c.wa_id}#inbox` }, t("Chat")), " ",
        canWrite && h("button", { class: "link", onclick: () => form(c) }, t("Edit"))))))) : h("p", { class: "muted" }, t("No contacts")));
  }

  /** c: a contact to edit, or a new one (no id; may come pre-filled from a chat, which is then linked to it) */
  function form(c: C | null, fromChat?: { line: string; wa_id: string }) {
    const isNew = !c?.id;
    const name = h("input", { value: c?.name ?? "", dir: "auto", required: true });
    const phone = h("input", { value: c?.wa_id ? "+" + c.wa_id : "", placeholder: "+968 9xxx xxxx", dir: "ltr", inputmode: "tel" });
    const email = h("input", { type: "email", value: c?.email ?? "", dir: "ltr" });
    const lang = h("select", {}, [["", "—"], ["ar", "العربية"], ["en", "English"]].map(([v, l]) => h("option", { value: v, selected: (c?.lang ?? "") === v }, l)));
    const tags = h("input", { value: (c?.tags ?? []).join(", "), placeholder: t("e.g. buyer, villa") });
    const source = h("input", { value: c?.source ?? "", placeholder: t("e.g. walk-in, website, WhatsApp") });
    const notes = h("textarea", { rows: 2, dir: "auto" }, c?.notes ?? "");
    const ok = h("input", { type: "checkbox", checked: !!c?.marketing_ok });
    const how = h("input", { value: c?.marketing_how ?? "", placeholder: t("How they agreed: form, verbal, WhatsApp …") });
    mount(edit, h("div", { class: "card-in" },
      h("h3", {}, isNew ? t("New contact") : t("Edit contact")),
      h("div", { class: "row" }, h("label", {}, t("Name"), name), h("label", {}, t("WhatsApp number"), phone)),
      h("div", { class: "row" }, h("label", {}, t("Email"), email), h("label", {}, t("Language"), lang)),
      h("div", { class: "row" }, h("label", {}, t("Tags"), tags), h("label", {}, t("Source"), source)),
      h("label", {}, t("Notes"), notes),
      h("div", { class: "consent" }, h("label", { class: "inline" }, ok, " " + t("Agrees to marketing messages")), h("label", {}, t("Opt-in source"), how),
        c?.marketing_at && h("small", { class: "muted" }, `${t("Last change")}: ${fmtTime(c.marketing_at)} · ${c.marketing_by ?? ""}`),
        c?.wa_stop_at && h("small", { class: "err" }, t("Replied STOP on {date}: ticking the box needs their new agreement.", { date: fmtTime(c.wa_stop_at) }))),
      h("div", { class: "actions" },
        !isNew && role === "admin" && h("button", { onclick: async () => {
          if (!confirm(t("Delete this contact? (Kept for the audit, hidden from the team.)"))) return;
          try { await rpc("delete_contact", { p_contact: c!.id }); mount(edit); load(); } catch (e) { toast((e as Error).message, true); }
        } }, t("Delete")),
        h("button", { onclick: () => mount(edit) }, t("Cancel")),
        h("button", { class: "primary", onclick: async () => {
          const wa = phone.value.trim() ? waPhone(phone.value, cc) : null;
          if (phone.value.trim() && !wa) return toast(t("WhatsApp number: digits with country code"), true);
          if (!name.value.trim()) return toast(t("Name is required"), true);
          const row = { name: name.value.trim(), wa_id: wa, email: email.value.trim() || null, lang: lang.value || null,
            tags: tags.value.split(",").map((x) => x.trim()).filter(Boolean), source: source.value.trim() || null, notes: notes.value.trim() || null };
          const { data, error } = !isNew ? await sb.from("contacts").update({ ...row, updated_at: new Date().toISOString() }).eq("id", c!.id).select("id").single()
            : await sb.from("contacts").insert(row).select("id").single();
          if (error) return toast(/contacts_wa_id_live/.test(error.message) ? t("Another contact has this number") : error.message, true);
          if (ok.checked !== !!c?.marketing_ok || (ok.checked && how.value.trim() !== (c?.marketing_how ?? ""))) {
            try { await rpc("set_marketing_consent", { p_contact: data.id, p_ok: ok.checked, p_how: how.value }); }
            catch (e) { toast((e as Error).message, true); return; }
          }
          if (isNew && fromChat && wa === fromChat.wa_id) await rpc("link_chat_contact", { p_line: fromChat.line, p_wa_id: wa, p_contact: data.id }).catch(() => {});
          toast(t("Saved")); mount(edit); load();
        } }, t("Save")))));
    name.focus();
  }

  await load();
  // "+ Contact" from a chat: ?new=<number>&line=<our number>
  const p = new URLSearchParams(location.search), wa = (p.get("new") ?? "").replace(/\D/g, ""), line = (p.get("line") ?? "").replace(/\D/g, "");
  if (wa && canWrite) {
    history.replaceState(null, "", location.pathname + location.hash);
    form({ id: "", name: p.get("name") ?? "", wa_id: wa, email: null, lang: null, tags: [], notes: null, source: "WhatsApp", marketing_ok: false,
      marketing_at: null, marketing_by: null, marketing_how: null, wa_stop_at: null, created_at: "" }, line ? { line, wa_id: wa } : undefined);
  }
}
